import React, { useEffect, useState } from 'react';
import {generateRandomString, hexToRGB} from 'utils';
import { SubLayout } from 'layouts';
import { useViewport } from 'hooks';
import { priceColors, priceList } from 'data';
import { useNavigate } from 'react-router-dom';
import {Helmet} from "react-helmet";
import { useTranslation } from 'react-i18next';
import { Button, ColorCard, Icon, Modal, Switch, Alert, Img } from 'components';
import {
  Box,
  CardTag,
  ColBox,
  Container,
  CustomH3,
  FlexList,
  FlexListItem,
  GridList,
  ListItem,
  RowBox,
  Span,
  Text,
} from 'styles';

import {useAsync} from "react-use";
import OrderService from 'services/order/OrderService';
import cloneDeep from 'lodash/cloneDeep';
import userAuthenticationStore from "../../stores/AuthenticationStore";

export function Pricing(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { width } = useViewport();

  const userInfo = userAuthenticationStore((state) => state.userInfo) || {};

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessageOpen, setModalMessageOpen] = useState(false);
  const [periodType, setPeriodType] = useState('PT01');
  const [discountPriceYn, setDiscountPriceYn] = useState('N');
  const [products, setProducts] = useState([]);
  const [filterProducts, setFilterProducts] = useState([]);
  const [monthProducts, setMonthProducts] = useState([]);
  const [yearProducts, setYearProducts] = useState([]);
  const [subscribeProductCd, setSubscribeProductCd] = useState(null);
  const [subscribeProduct, setSubscribeProduct] = useState(null);

  const [alert, setAlert] = useState(false);

  const onChangePeriodType = () => {
    setPeriodType((_type) => (_type === 'PT01' ? 'PT02' : 'PT01'));
  };

  const getProductLevel = (productCd)=>{
    if(productCd === 'F01'){
      return 0;
    }else if(productCd === 'B01' || productCd === 'B02'){
      return 1;
    }else if(productCd === 'S01' || productCd === 'S02'){
      return 2;
    }else if(productCd === 'P01' || productCd === 'P02'){
      return 3;
    }else if(productCd === 'E01' || productCd === 'E02'){
      return 4;
    }
    return -1;
  }

  const goPayment = (selectedProduct) => {
    if(subscribeProductCd != null){
      if(getProductLevel(subscribeProductCd) > getProductLevel(selectedProduct.productCd)){
        setModalMessageOpen(true);
      }else{
        navigate('/pricing/payment', { state: { productType: selectedProduct } });
      }
    }else{
      navigate('/pricing/payment', { state: { productType: selectedProduct } });
    }

  };

  const buildProductPriceData = (obj, list)=>{
    const rec = cloneDeep(obj);

    const product = list.find((o) => o.productCd === rec.productCd);
    if(product){
      if(product.productCd !== 'F01'){
        rec.cost = product.price;
        rec.discount = product.salePrice;
        rec.salePrice = product.salePrice;
      }else{
        rec.salePrice = 0;
      }

    }
    return rec;
  }

  useAsync(async () => {
    const responses = await OrderService.getAllProducts();

    if(responses.status === 200){
      const list = responses.data.products;
      setProducts(list);
      if(responses.data.subscribeProductCd){
        const product = responses.data.products.find((p)=>p.productCd === responses.data.subscribeProductCd);
        setSubscribeProduct(product);
      }

      setDiscountPriceYn(responses.data.discountPriceYn);
      setSubscribeProductCd(responses.data.subscribeProductCd);
      const monthPriceData =
        priceList.find((plan) => plan.type === "PT01").content.map((obj)=>buildProductPriceData(obj, list));

      const yearPriceData =
        priceList.find((plan) => plan.type === "PT02").content.map((obj)=>buildProductPriceData(obj, list));

      setMonthProducts(monthPriceData);
      setYearProducts(yearPriceData);
    }


  }, [userInfo.id]);

  useAsync(async () => {
    console.log(periodType);
    if(periodType === 'PT01'){
      setFilterProducts(monthProducts);
    }else{
      setFilterProducts(yearProducts);
    }

  }, [periodType, monthProducts, yearProducts]);

  return (
    <>
      <Helmet>
        <meta name="keywords" content={t('meta.keywords.pricing')}/>
      </Helmet>

      <SubLayout h2="Pricing" moH2="Plans & Pricing" path1="Pricing">
        <Container $padding="50px 20px 128px" $tPadding="40px 17px 88px" $margin="auto">
          <CustomH3 $tDisplay="none">PRICING TABLE</CustomH3>
          <Switch
            checked={periodType === 'PT02'}
            onChange={onChangePeriodType}
            label1="Billed Monthly"
            label2={
              <>
                Billed Yearly&nbsp;
                <Span $tType="14" $color={periodType === 'PT02' && hexToRGB('orange', 0.8)}>
                  (15~30% off)
                </Span>
              </>
            }
          />
          <GridList $grid={5} $gap={10} $tGrid={1} $tGap={30} $tPadding="0 16px" $tMarginTop={24}>
            {filterProducts && filterProducts?.map((con, index) => (
                <ListItem key={`con_${index}`}>
                  <ColorCard $bgColor={priceColors[index]} $height="100%">
                    <Box $padding="10px" $tPadding="16px">
                      <Text $type="14" $mType="m18" $textAlign="center" $tTextAlign="start">
                        {con.type}
                      </Text>
                      <Text $paddingTop={4} $type="b38" $textAlign="center" $tTextAlign="start" $color="black70">
                        ${con.salePrice?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        {width <= 1023 && discountPriceYn === 'Y' && (
                          <Span
                            $type="m14"
                            $paddingLeft={4}
                            $color="black30"
                            $textDecoration={con.productCd !== 'F01' && 'line-through'}
                          >
                            {con.productCd !== 'F01' && `$`}
                            {con.productCd === 'F01' ? 'FREE' : con.cost.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          </Span>
                        )}
                      </Text>
                      {width > 1023 && discountPriceYn === 'Y' && (
                        <Text
                          $padding="4px 0"
                          $type="m18"
                          $textAlign="center"
                          $tTextAlign="start"
                          $color="black30"
                          $textDecoration={con.productCd !== 'F01' && 'line-through'}
                        >
                          {con.productCd !== 'F01' && `$`}
                          {con.productCd === 'F01' ? 'FREE' : con.cost.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        </Text>
                      )}
                      <Text
                        $tMarginTop={4}
                        $type="14"
                        $tType="16"
                        $textAlign="center"
                        $tTextAlign="start"
                        $color="black19"
                      >
                        {con.period}
                      </Text>
                      <Button
                        onClick={() => goPayment(con)}
                        $margin="16px 0 0 0"
                        $tMargin="8px 0 0 0"
                        $width="100%"
                        $height={36}
                        $tHeight={44}
                        $bgColor={priceColors[index]}
                      >
                        Choose {con.type}
                      </Button>
                      <FlexList $marginTop={4} $gap={4} $flexDirection="column">
                        {con.list.map((item, index) => (
                          <FlexListItem
                            key={`item_${index}`}
                            $alignItems={index === 0 ? 'flex-start' : 'center'}
                            $justifyContent="space-between"
                            $padding="8px 0"
                            $tPadding="4px 0"
                          >
                            <RowBox $gap={4}>
                              <Icon name="CardCheck" />
                              <Text $type="m12" $color="black19">
                                {item.service && <CardTag $type="sb10">{item.service}</CardTag>}
                                {item.text}
                                <br />
                                {item.desc && (
                                  <Span $type="m12" $color="orange" $display="inline-block">
                                    {item.desc}
                                  </Span>
                                )}
                              </Text>
                            </RowBox>
                            {item.info && <Icon name="Info" $padding="2px 0" onClick={() => setModalOpen(item.text)} />}
                            {/* {item.info && <Tooltip />} */}
                          </FlexListItem>
                        ))}
                      </FlexList>
                    </Box>
                  </ColorCard>
                </ListItem>
              ))}
          </GridList>
        </Container>
      </SubLayout>

      <Modal open={modalOpen} setOpen={setModalOpen} $path="price" title={modalOpen} mNotClose>
        <Box $width={540} $tWidth="100%" $tMinWidth="100%">
          <Box $tOverflowY="auto" $mPadding="8px 22px 8px 8px" $tMaxHeight="calc(70vh - 160px)">
            <Text $color="gray800" $type="b18" $paddingBottom={16} $tPaddingBottom={8}>
              {modalOpen === 'Route Recommendation'
                ? 'AI-based Voyage simulation for search routes'
                : modalOpen === 'Weather Display'
                  ? 'GFS wave and ECMWF model'
                  : 'GFS wave and ECMWF model 3,000 ports, 216 hours'}
            </Text>
            {modalOpen === 'Route Recommendation' && (
              <Text $color="gray800" $paddingBottom={16}>
                Recommended route, standard route and alternative routes
              </Text>
            )}
            {modalOpen === 'Route Recommendation' && (
              <Img src="pricing1" $imgWidth="100%" $minHeight={275} $tMinHeight={140} />
            )}
            {modalOpen === 'Weather Display' && (
              <Img src="pricing2" $imgWidth="100%" $minHeight={340} $tMinHeight={170} />
            )}
            {modalOpen === 'Port Forecast' && (
              <Img src="pricing3" $imgWidth="100%" $minHeight={400} $tMinHeight={204} />
            )}
            {modalOpen === 'Route Recommendation' && (
              <ColBox $gap={16} $paddingTop={16} $tPaddingTop={8} $tGap={8}>
                <Box>
                  <Text $type="b" $color="gray800">
                    Routes comparison
                  </Text>
                  <Text $color="gray800">
                    distance sailing, ETA, time en route, engine speed, weather factor, current factor, average speed
                  </Text>
                </Box>
                <Box>
                  <Text $type="b" $color="gray800">
                    Route forecast
                  </Text>
                  <Text $color="gray800">
                    date, UTC, latitude, longitude, distance, engine speed, heading, beaufort scale, wind speed, wind
                    direction, wave height, wave direction, current, current direction
                  </Text>
                </Box>
                <Box>
                  <Text $type="b" $color="gray800">
                    Limit option
                  </Text>
                  <Text $color="gray800">wave, wind and beaufort scale</Text>
                </Box>
              </ColBox>
            )}
            {modalOpen === 'Weather Display' && (
              <ColBox $gap={12} $paddingTop={16} $tPaddingTop={8} $tGap={8}>
                <Text $type="b" $color="gray800">
                  Wave height, wave direction
                  <Span $paddingLeft={8} $color="gray800">
                    GFS
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Surface pressure
                  <Span $paddingLeft={8} $color="gray800">
                    GFS
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Current
                  <Span $paddingLeft={8} $color="gray800">
                    HYCOM
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Ice cover
                  <Span $paddingLeft={8} $color="gray800">
                    GFS ensemble wave
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Visibility
                  <Span $paddingLeft={8} $color="gray800">
                    GDAPs
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Wind barbs
                  <Span $paddingLeft={8} $color="gray800">
                    GFS, ECMWF
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Swell height, swell direction
                  <Span $paddingLeft={8} $color="gray800">
                    GFS wave
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Tropical storm
                  <Span $paddingLeft={8} $color="gray800">
                    JTWC, NHC
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  500hPa height
                  <Span $paddingLeft={8} $color="gray800">
                    GFS
                  </Span>
                </Text>
              </ColBox>
            )}
            {modalOpen === 'Port Forecast' && (
              <ColBox $gap={12} $paddingTop={16} $tPaddingTop={8} $tGap={8}>
                <Text $type="b" $color="gray800">
                  Wind speed, wind direction, gust wind
                  <Span $paddingLeft={8} $color="gray800">
                    GFS
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Wave direction, maximum wave height, significant wave height, significant wave period
                  <Span $paddingLeft={8} $color="gray800">
                    GFS
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Current
                  <Span $paddingLeft={8} $color="gray800">
                    HYCOM
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Weather condition
                  <Span $paddingLeft={8} $color="gray800">
                    GFS
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Visibility
                  <Span $paddingLeft={8} $color="gray800">
                    GDAPs
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Port name
                  <Span $paddingLeft={8} $color="gray800"></Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Swell height, swell direction
                  <Span $paddingLeft={8} $color="gray800">
                    GFS wave
                  </Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Country
                  <Span $paddingLeft={8} $color="gray800"></Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Location
                  <Span $paddingLeft={8} $color="gray800"></Span>
                </Text>
                <Text $type="b" $color="gray800">
                  Time(UTC and local)
                  <Span $paddingLeft={8} $color="gray800"></Span>
                </Text>
              </ColBox>
            )}
          </Box>
          <Box $paddingTop={32} $tPaddingTop={16}>
            <Button mode="line" $width="100%" $height={44} $type="b" $color="gray900" onClick={() => setModalOpen()}>
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={modalMessageOpen} setOpen={setModalMessageOpen} $path="price" notClose title="Message">
        <Box $margin="0 0 28px" $minWidth={404} $tMinWidth="100%" $tMaxHeight={237} $overflowY="auto">
          <Text>You are subscribing to <b>{subscribeProduct?.productName}</b> service.</Text>
          <Text>So you cannot subscribe to a lower-level service.</Text>
        </Box>
        <Button mode="line" $width="100%" $height={44} $type="b" onClick={() => setModalMessageOpen(false)}>
          OK
        </Button>
      </Modal>

    </>
  );
}
